import {
  SignIn as ClerkSignIn,
  SignUp as ClerkSignUp,
  useUser,
} from "@clerk/clerk-react";
import { useSearchParams } from "../../hooks/useRouter";
import { useEffect } from "react";

const ALLOWED_REDIRECT_ORIGINS = [
  "https://meet.withstoa.com",
  "https://meet-dev.withstoa.com",
  "http://localhost:3000",
  "http://localhost:3001",
];

function getSafeRedirectUrl(raw: string | null): string {
  const fallback = "https://meet.withstoa.com";
  if (!raw) return fallback;

  try {
    const url = new URL(raw);
    if (ALLOWED_REDIRECT_ORIGINS.some((o) => url.origin === o)) {
      return raw;
    }
  } catch {
    // relative path — treat as same-origin, but for Stoa pages this shouldn't happen
  }

  return fallback;
}

const STOA_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";

const STOA_APPEARANCE = {
  layout: {
    logoImageUrl: "/stoa_logo.png",
    logoLinkUrl: "https://withstoa.com",
  },
  variables: {
    colorPrimary: "#4F46E5",
    colorBackground: "#FAF9F7",
    colorInputBackground: "#FFFFFF",
    colorInputText: "#2D2926",
    colorText: "#2D2926",
    colorTextSecondary: "#78716C",
    colorDanger: "#DC2626",
    fontFamily: STOA_FONT_FAMILY,
    fontSize: "0.875rem",
    borderRadius: "8px",
  },
  elements: {
    // Hide Clerk's built-in header — we render our own above the component
    // so we're not dependent on ClerkProvider's localization text
    header: { display: "none" },
    rootBox: {
      width: "100%",
    },
    card: {
      backgroundColor: "#FFFFFF",
      border: "1px solid #E8E5E1",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
    },
    socialButtonsBlockButton: {
      backgroundColor: "#FFFFFF",
      border: "1px solid #E8E5E1",
      color: "#2D2926",
      "&:hover": {
        borderColor: "#4F46E5",
        backgroundColor: "#F5F3FF",
      },
    },
    formFieldLabel: {
      color: "#57534E",
      fontFamily: STOA_FONT_FAMILY,
      fontSize: "0.875rem",
    },
    formFieldInput: {
      backgroundColor: "#FFFFFF",
      border: "1px solid #E8E5E1",
      color: "#2D2926",
      fontFamily: STOA_FONT_FAMILY,
      "&:focus": {
        borderColor: "#4F46E5",
        boxShadow: "0 0 0 1px #4F46E5",
      },
    },
    formButtonPrimary: {
      backgroundColor: "#4F46E5",
      color: "#FFFFFF",
      fontFamily: STOA_FONT_FAMILY,
      border: "none",
      fontSize: "0.875rem",
      fontWeight: "500",
      "&:hover": {
        backgroundColor: "#4338CA",
      },
    },
    footerActionLink: {
      color: "#4F46E5",
      fontFamily: STOA_FONT_FAMILY,
      "&:hover": {
        color: "#4338CA",
      },
    },
    formFieldInputShowPasswordButton: {
      color: "#78716C",
      "&:hover": {
        color: "#4F46E5",
      },
    },
    identityPreviewText: {
      color: "#2D2926",
      fontFamily: STOA_FONT_FAMILY,
    },
    identityPreviewEditButton: {
      color: "#4F46E5",
      "&:hover": {
        color: "#4338CA",
      },
    },
  },
};

interface StoaAuthPageProps {
  mode: "sign-in" | "sign-up";
}

export const StoaAuthPage = ({ mode }: StoaAuthPageProps) => {
  const searchParams = useSearchParams();
  const redirectUrl = getSafeRedirectUrl(searchParams.get("redirect_url"));
  const { isLoaded, isSignedIn } = useUser();

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      window.location.href = redirectUrl;
    }
  }, [isLoaded, isSignedIn, redirectUrl]);

  if (!isLoaded || isSignedIn) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <div className="text-[#78716C] text-sm">Loading...</div>
      </div>
    );
  }

  // Build cross-link URLs preserving the redirect_url param
  const redirectParam = searchParams.get("redirect_url");
  const crossLinkSuffix = redirectParam
    ? `?redirect_url=${encodeURIComponent(redirectParam)}`
    : "";

  // After auth, Clerk redirects back to this same page (same-origin) so the
  // isSignedIn useEffect above can handle the cross-domain redirect to Stoa.
  // Clerk's forceRedirectUrl doesn't reliably redirect cross-domain.
  const selfUrl =
    mode === "sign-in"
      ? `/stoa-sign-in${crossLinkSuffix}`
      : `/stoa-sign-up${crossLinkSuffix}`;

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex flex-col relative overflow-hidden">
      {/* Force Clerk card centering */}
      <style>{`.cl-rootBox { display: flex !important; justify-content: center !important; }`}</style>

      {/* Grid texture — matches Stoa landing page */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 40%, black 20%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 60% at 50% 40%, black 20%, transparent 70%)",
        }}
      />

      <main className="relative flex-1 flex items-center justify-center px-4 sm:px-6 py-8">
        <div className="w-full max-w-md text-center">
          {/* Brand header */}
          <div className="mb-8">
            <a
              href="https://withstoa.com"
              className="font-sans text-sm font-medium tracking-[0.2em] uppercase text-[#78716C] hover:text-[#57534E] transition-colors pl-[0.2em]"
            >
              Stoa by SpecStory
            </a>
          </div>

          {/* Glow behind the Clerk card */}
          <div className="relative">
            <div className="absolute -inset-8 rounded-3xl bg-gradient-to-b from-indigo-400/[0.07] via-transparent to-transparent blur-2xl pointer-events-none" />

            {mode === "sign-in" ? (
              <ClerkSignIn
                appearance={STOA_APPEARANCE}
                forceRedirectUrl={selfUrl}
                fallbackRedirectUrl={selfUrl}
                signUpUrl={`/stoa-sign-up${crossLinkSuffix}`}
                signUpForceRedirectUrl={selfUrl}
                signUpFallbackRedirectUrl={selfUrl}
              />
            ) : (
              <ClerkSignUp
                appearance={STOA_APPEARANCE}
                forceRedirectUrl={selfUrl}
                fallbackRedirectUrl={selfUrl}
                signInUrl={`/stoa-sign-in${crossLinkSuffix}`}
                signInForceRedirectUrl={selfUrl}
                signInFallbackRedirectUrl={selfUrl}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
